import { useEffect, useRef, useCallback } from "react";
import { GamepadPollingManager } from "../GamepadPollingManager";
import type {
  GamepadButtonEvent,
  GamepadAxisEvent,
  GamepadConnectionEvent,
  EnhancedGamepad,
} from "../types";

export interface UseGamepadPollingOptions {
  pollRate?: number;
  deadZone?: number;
  debug?: boolean;
  priority?: number;
  onButton?: (event: GamepadButtonEvent) => void;
  onAxis?: (event: GamepadAxisEvent) => void;
  onConnect?: (event: GamepadConnectionEvent) => void;
  onDisconnect?: (event: GamepadConnectionEvent) => void;
  autoStart?: boolean;
}

export interface UseGamepadPollingReturn {
  startPolling: () => void;
  stopPolling: () => void;
  getEnhancedGamepad: (index: number) => EnhancedGamepad | undefined;
  getAllEnhancedGamepads: () => EnhancedGamepad[];
  getGamepadObject: (index: number) => Gamepad | null;
  getGamepads: () => (Gamepad | null)[];
  isPolling: boolean;
}

/**
 * React hook for using the centralized gamepad polling system
 */
export function useGamepadPolling(
  options: UseGamepadPollingOptions = {},
): UseGamepadPollingReturn {
  const {
    pollRate = 60,
    deadZone = 0.05,
    debug = false,
    priority = 0,
    onButton,
    onAxis,
    onConnect,
    onDisconnect,
    autoStart = true,
  } = options;

  const managerRef = useRef<GamepadPollingManager | null>(null);
  const listenerIdsRef = useRef<string[]>([]);
  const isPollingRef = useRef(false);

  // Initialize manager
  useEffect(() => {
    managerRef.current = GamepadPollingManager.getInstance({
      pollRate,
      deadZone,
      debug,
    });

    return () => {
      // Clean up listeners on unmount
      if (managerRef.current) {
        listenerIdsRef.current.forEach((id) => {
          managerRef.current?.removeEventListener(id);
        });
        listenerIdsRef.current = [];
      }
    };
  }, []);

  // Setup event listeners
  useEffect(() => {
    const manager = managerRef.current;
    if (!manager) return;

    // Clear existing listeners
    listenerIdsRef.current.forEach((id) => manager.removeEventListener(id));
    listenerIdsRef.current = [];

    // Add new listeners
    if (onButton) {
      const id = manager.addEventListener("button", onButton, { priority });
      listenerIdsRef.current.push(id);
    }

    if (onAxis) {
      const id = manager.addEventListener("axis", onAxis, { priority });
      listenerIdsRef.current.push(id);
    }

    if (onConnect) {
      const id = manager.addEventListener("connect", onConnect, { priority });
      listenerIdsRef.current.push(id);
    }

    if (onDisconnect) {
      const id = manager.addEventListener("disconnect", onDisconnect, {
        priority,
      });
      listenerIdsRef.current.push(id);
    }

    return () => {
      listenerIdsRef.current.forEach((id) => manager.removeEventListener(id));
      listenerIdsRef.current = [];
    };
  }, [onButton, onAxis, onConnect, onDisconnect, priority]);

  // Auto-start polling
  useEffect(() => {
    if (autoStart && managerRef.current) {
      managerRef.current.startPolling();
      isPollingRef.current = true;
    }
  }, [autoStart]);

  const startPolling = useCallback(() => {
    if (managerRef.current) {
      managerRef.current.startPolling();
      isPollingRef.current = true;
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (managerRef.current) {
      managerRef.current.stopPolling();
      isPollingRef.current = false;
    }
  }, []);

  const getEnhancedGamepad = useCallback((index: number) => {
    return managerRef.current?.getEnhancedGamepad(index);
  }, []);

  const getAllEnhancedGamepads = useCallback(() => {
    return managerRef.current?.getAllEnhancedGamepads() || [];
  }, []);

  const getGamepadObject = useCallback((index: number) => {
    return managerRef.current?.getGamepadObject(index) || null;
  }, []);

  const getGamepads = useCallback(() => {
    return managerRef.current?.getGamepads() || [null, null, null, null];
  }, []);

  // Removed getGamepadList - use getGamepads() instead

  return {
    startPolling,
    stopPolling,
    getEnhancedGamepad,
    getAllEnhancedGamepads,
    getGamepadObject,
    getGamepads,
    isPolling: isPollingRef.current,
  };
}
